import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Box, Typography, Skeleton } from '@mui/material';
import { TrendingUp as TrendingUpIcon, Error as ErrorIcon, Groups as GroupsIcon } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useCountAnimation } from '../../hooks/useCountAnimation';

const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const ProgressLevelCard = ({ currentLevel, patientsUntilNextLevel, isLoading, error }) => {
  const { t } = useTranslation();  // Translation hook

  // calculating progress percentage for more interactivity
  const totalForLevel = currentLevel * 10;
  const currentProgress = totalForLevel - patientsUntilNextLevel;
  const progressPercentage = (currentProgress / totalForLevel) * 100;
  const animatedProgress = useCountAnimation(currentProgress, 1000);

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 15px 50px rgba(102, 126, 234, 0.4)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      {/* Background decoration circles */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          filter: 'blur(40px)',
        }}
      />

      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          >
            {t('practiceGrowth')}
          </Typography>

          {/* Growth Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrendingUpIcon sx={{ color: 'white', fontSize: '1.5rem' }} />
          </Box>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <>
            <Skeleton
              variant="rectangular"
              width={100}
              height={100}
              sx={{ mb: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }}
            />
            <Skeleton
              variant="text"
              width={180}
              height={24}
              sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }}
            />
            <Skeleton
              variant="rounded"
              width="100%"
              height={12}
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}
            />
          </>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <ErrorIcon sx={{ fontSize: '3rem', color: 'rgba(255, 255, 255, 0.9)', mb: 2 }} />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {t('errorLoading')}
            </Typography>
          </Box>
        )}

        {/* Success State */}
        {!isLoading && !error && (
          <>
            {/* Patient Count with Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              {/* Icon Background */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GroupsIcon
                  sx={{
                    fontSize: '3rem',
                    color: 'white',
                    filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
                  }}
                />
              </Box>

              {/* Milestone Number */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '4.5rem',
                    fontWeight: 800,
                    color: 'white',
                    lineHeight: 1,
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {animatedProgress}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                >
                  {t('patients')}
                </Typography>
              </Box>
            </Box>

            {/* Progress Text */}
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                mb: 2,
                fontSize: '0.95rem',
                fontWeight: 500,
              }}
            >
              {patientsUntilNextLevel === 0
                ? t('milestoneAchieved')
                : t('patientsToNextMilestone', { count: patientsUntilNextLevel })
              }
            </Typography>

            {/* Progress Bar Container */}
            <Box sx={{ position: 'relative', mb: 1 }}>
              {/* Background Progress Bar */}
              <Box
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Animated Progress Fill */}
                <Box
                  sx={{
                    height: '100%',
                    width: `${progressPercentage}%`,
                    background: 'linear-gradient(90deg, #10B981, #34D399, #10B981)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmerAnimation} 3s linear infinite`,
                    borderRadius: 6,
                    position: 'relative',
                    transition: 'width 1s ease-out',
                    boxShadow: '0 0 20px rgba(16, 185, 129, 0.5)',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
                      borderRadius: '6px 6px 0 0',
                    },
                  }}
                />
              </Box>

              {/* Progress Percentage Text */}
              <Typography
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: -28,
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>

            {/* Progress Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                }}
              >
                {t('currentPatients', { count: animatedProgress })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {t('target', { count: totalForLevel })}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProgressLevelCard;